import { Box, Grid, SelectChangeEvent, Stack, Typography, useTheme, useMediaQuery } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { useEffect, useState, useRef, useMemo } from 'react';
import DBsqlIcon from '../../assets/ph_file-sql.svg';
import moment from 'moment-timezone';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import { CustomizedDataGrid } from '../customized_data_grid';
import { totalSqlData, totalCostData, newWarehouseData, costTrendData, topDBUData, topCostData, workspaceGetEnv, lonRunData, failedMetricsData, peakCountData, queryTrendData, topGroupDbuData, topGroupCostData, topUsageCatalogsData, sqllastFetchData, topUsageCost, fetchSectorList } from 'service/service';
import LoaderComponent from 'components/Loader';
import NumberWidget from 'components/Widgets/NumberWidget';
import { SeriesOption } from 'echarts';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { useLocation, useNavigate } from 'react-router-dom';
import FiltersBar from './FilterBar';
import { toTitleCase } from './workspace';
import PageWrapper from 'components/Common/PageWraper';
import ChartWidgetWithTotal from '../DatabricksADB/ChartWidgetWithTotal';
import { useAdbBaseState } from 'utils/adb-common-util';

const DashboardCard = ({
    id,
    md = 6,
    isLoading,
    children,
    height = '380px'
}: {
    id: string;
    md?: number;
    isLoading: boolean;
    children: React.ReactNode;
    height?: string;
}) => (
    <Grid item xs={12} md={md} id={id}>
        <Box sx={{ height: height, width: '100%', display: 'flex', flexDirection: 'column' }}>
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%" bgcolor="#FFFFFF" borderRadius="5px">
                    <LoaderComponent size={50} />
                </Box>
            ) : (
                children
            )}
        </Box>
    </Grid>
);

const ChartSection = ({
    title,
    option,
    onClick,
}: {
    title: string;
    option: any;
    onClick: (params: any) => void;
}) => (
    <Stack p={2} bgcolor="#FFFFFF" fontFamily="Montserrat" borderRadius="5px" height="100%" sx={{ overflow: 'hidden' }}>
        <Typography sx={{ fontSize: { xs: '14px', lg: '16px' }, mb: 1 }} fontWeight={500} color="#000000">
            <HighlightedText text={title} />
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <ReactECharts
                option={option}
                style={{ height: '100%', width: '100%' }}
                onEvents={{ click: onClick }}
            />
        </Box>
    </Stack>
);

function getMonthRange(dateString: string) {
    const inputDate = new Date(dateString);
    const now = new Date();

    const year = inputDate.getFullYear();
    const month = inputDate.getMonth();

    // Start of month
    const start = new Date(year, month, 1);

    let end;

    // If input month = current month → end = yesterday
    if (year === now.getFullYear() && month === now.getMonth()) {
        end = new Date(now);
        end.setDate(end.getDate() - 1);
    } else {
        // Otherwise last day of month
        end = new Date(year, month + 1, 0);
    }

    // Format without timezone shifting (manual YYYY-MM-DD)
    const format = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    return {
        startDate: format(start),
        endDate: format(end)
    };
}

const createLegendConfig = (isSmallScreen: boolean, colorMapOrNames: Record<string, string> | string[] = {}) => {
    let data: string[] = [];
    if (Array.isArray(colorMapOrNames)) {
        data = colorMapOrNames;
    } else {
        data = Object.keys(colorMapOrNames).map(key => key.charAt(0) + key.slice(1).toLowerCase());
    }

    return {
        orient: isSmallScreen ? 'vertical' : 'horizontal',
        bottom: isSmallScreen ? 0 : '3%',
        left: 'center',
        textStyle: { color: '#000000', fontSize: isSmallScreen ? 10 : 12 },
        data,
    };
};

const createSeriesLabel = (
    isSmallScreen: boolean,
    valueFormatter: (v: number) => string,
    fontSizeSmall = 11,
    fontSizeLarge = 15,
    forceShow?: boolean
) => ({
    show: typeof forceShow === 'boolean' ? forceShow : !isSmallScreen,
    position: 'inside',
    color: '#fff',
    fontSize: isSmallScreen ? fontSizeSmall : fontSizeLarge,
    formatter: (params: any) => {
        const value = Number(params.value ?? 0);
        return valueFormatter(value);
    }
});

const widgetOnClickHandler = (chartType: string, opts: {
    navigate: any;
    startDate: any;
    endDate: any;
    env: string;
    sectorName: string;
    location: string;
    selectedDateInString: string;
    PRESERVE_FILTER_KEY: string;
}) => {
    const defaultConfig = {
        detailedTableTitle: 'Failed Queries Details',
        detailedTableType: 'failed-queries-detail',
        pageSubHeader: 'Failed Queries Details',
        navigateTo: '/databricksADB/performance-dashboard'
    };

    const overrides: Record<string, Partial<typeof defaultConfig>> = {
        peakConcurrencyQueryCount: {
            detailedTableTitle: 'Peak Concurrency Query Details',
            detailedTableType: 'peak-concurrency-query-detail',
            pageSubHeader: 'Peak Concurrency Query Details',
            navigateTo: '/databricksADB/SQLDetails'
        }
    };

    const cfg = { ...defaultConfig, ...overrides[chartType] };

    localStorage.removeItem(cfg.detailedTableType);
    const snap = {
        startDate: opts.startDate,
        endDate: opts.endDate,
        selectedDateInString: opts.selectedDateInString,
    };
    sessionStorage.setItem(opts.PRESERVE_FILTER_KEY, JSON.stringify(snap));

    opts.navigate(cfg.navigateTo, {
        state: {
            startDate: opts.startDate,
            endDate: opts.endDate,
            env: opts.env,
            sector: opts.sectorName,
            location: opts.location,
            selectedDateInString: opts.selectedDateInString,
            searchSource: chartType,
            searchPoint: null,
            detailedTableTitle: cfg.detailedTableTitle,
            detailedTableType: cfg.detailedTableType,
            pageHeader: 'DBSQL Performance',
            pageSubHeader: cfg.pageSubHeader,
        }
    });
};

const DBsql = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const chartRadius = useMemo(() => {
        if (isSmallScreen) return "60%";
        if (isMediumScreen) return "68%";
        return "72%";
    }, [isSmallScreen, isMediumScreen]);
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const navState = routerLocation?.state || {};
    const navigationState = routerLocation.state as {
        startDate?: string;
        endDate?: string;
        env?: string;
        sector?: string;
        location?: string;
        selectedDateInString?: string;
        searchSource?: string;
    } | null;

    const {
        restoredRef,
        PRESERVE_FILTER_KEY,
        ...defaults
    } = useAdbBaseState(navigationState, 'dbsql_filters');
    const DEFAULT_FILTERS = defaults;

    const [sector, setSector] = useState<string>(DEFAULT_FILTERS.sector);
    const [sectorName, setSectorName] = useState(navigationState?.sector || 'all');
    const [location, setLocation] = useState(DEFAULT_FILTERS.location);
    const [env, setEnv] = useState(DEFAULT_FILTERS.env);
    const [startDate, setStartDate] = useState<string>(DEFAULT_FILTERS.startDate);
    const [endDate, setEndDate] = useState<string>(DEFAULT_FILTERS.endDate);
    const [selectedDateInString, setSelectedDateInString] = useState<string>(DEFAULT_FILTERS.selectedDateInString);
    const [sectorList, setSectorList] = useState<string[]>([]);

    const fetchSectors = async () => {
        const resp = await fetchSectorList({});
        if (resp?.success) {
            setSectorList(resp?.data || []);
        }
    };

    useEffect(() => {
        fetchSectors()
    }, [])

    const handleDateSelect = (stDt: string, edDt: string, selectedDate?: string) => {
        const dt = selectedDate;
        const selected = dt || '3M';
        setSelectedDateInString(selected);
        const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
        const end = yesterday.clone().endOf('day');
        let start;

        if (selected === 'Hrs') {
            start = yesterday.clone().startOf('day');
        } else if (selected === '7d') {
            start = yesterday.clone().subtract(6, 'days').startOf('day');
        } else if (selected === '30d') {
            start = yesterday.clone().subtract(29, 'days').startOf('day');
        } else if (selected === '3M') {
            start = yesterday.clone().subtract(89, 'days').startOf('day');
        } else if (selected === 'custom') {
            start = moment(stDt).startOf('day');
            const customEnd = moment(edDt).endOf('day');
            setStartDate(start.format('YYYY-MM-DD'));
            setEndDate(customEnd.format('YYYY-MM-DD'));
            return;
        } else {
            start = moment(stDt).startOf('day');
        }
        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
    }

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = ['10', '20', '30', '40', '50'];
    const [columns, setColumns] = useState<any[]>([]);
    const [lastSync, setLastSync] = useState(null);
    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 2
    });
    const compactFormatter = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 2
    });
    const [loader, setLoader] = useState({
        totalSqlType: false,
        totalCostType: false,
        newWarehouseType: false,
        costTrendType: false,
        topDBUType: false,
        topCostType: false,
        env: false,
        longRunType: false,
        queryTrendType: false,
        topGroupDbuType: false,
        peakCountType: false,
        failedMetricsType: false,
        topGroupCostType: false,
        topUsageCatalogsData: false,
        topUsageLogsCost: false
    });

    const [totalSql, setTotalSql] = useState<{ _id: string; total_warehouses_sum: number }[]>([]);
    const [totalCost, setTotalCost] = useState<{ _id: string; total_cost: number }[]>([]);
    const [totalNewWarehouse, setTotalNewWarehouse] = useState<{ _id: string; total_warehouses_new: number }[]>([]);
    const [costTrend, setCostTrend] = useState<{ total_cost: number; date: string; warehouse_type: string }[]>([]);
    const [topDBU, setTopDBU] = useState<{ total_dbus: number; workspace_name: string }[]>([]);
    const [topCost, setTopCost] = useState<{ total_costs: number; workspace_name: string }[]>([]);
    const [longRun, setLongRun] = useState<any[]>([]);
    const [failedMetrics, setFailedMetrics] = useState<{
        total_queries: number;
        total_failed_queries: number;
        total_failed_query_cost: number;
        failurePercentage: number;
    } | null>(null);
    const [peakCount, setPeakCount] = useState<{ _id: null; peakCount: number } | null>(null);
    const [queryTrend, setQueryTrend] = useState<{ queries_count: number; date: string; warehouse_type: string }[]>([]);
    const [topGroupDbu, setTopGroupDbu] = useState<{ total_dbus: number; executed_by: string }[]>([]);
    const [topGroupCost, setTopGroupCost] = useState<{ total_cost_usd: number; executed_by: string }[]>([]);
    const [topUsageCatalogs, setTopUsageCatalogs] = useState<{ total_usage_count: number; usage_by: string }[]>([]);
    const [topUsageCatalogsCst, setTopUsageCatalogsCst] = useState<{ total_catalog_cost_usd: number; usage_by: string }[]>([]);


    const columnMapping: Record<string, string> = {
        workspace_name: 'Workspace Name',
        statement_id: 'Query ID',
        warehouse_name: 'Warehouse Name',
        duration_seconds: 'Duration',
        start_time: 'Start Time',
        end_time: 'End Time'
    };

    const generateFixedColumns = () => {
        return Object.entries(columnMapping).map(([key, displayName]) => ({
            key,
            name: displayName,
            width: 'auto',
        }));
    };

    const filterTableData = (data: any[]) => {
        return data.map((row) => {
            const filtered: Record<string, any> = {};
            for (const key of Object.keys(columnMapping)) {
                if (key === 'duration_seconds' && typeof row[key] === 'number') {
                    filtered[key] = formatDuration(row[key]); // Convert seconds to readable time
                } else if (key === 'start_time' && row[key]) {
                    filtered[key] = moment(row[key]).tz('America/Chicago').format('YYYY-MM-DD HH:mm:ss'); // Format date/time in CST
                } else if (key === 'end_time' && row[key]) {
                    filtered[key] = moment(row[key]).tz('America/Chicago').format('YYYY-MM-DD HH:mm:ss'); // Format date/time in CST
                } else {
                    filtered[key] = row[key] || '-';
                }
            }
            return filtered;
        });
    };

    // Helper to format minutes into days/hrs/mins/secs
    const formatDuration = (seconds: number) => {
        const days = Math.floor(seconds / (24 * 3600));
        seconds %= 24 * 3600;
        const hours = Math.floor(seconds / 3600);
        seconds %= 3600;
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);

        const parts = [];
        if (days) parts.push(`${days}d`);
        if (hours) parts.push(`${hours}h`);
        if (mins) parts.push(`${mins}m`);
        if (secs) parts.push(`${secs}s`);

        return parts.join(' ') || '0s';
    };

    const defaultColumns = ['Workspace Name', 'Query ID', 'Warehouse Name', 'Duration', 'Start Time', 'End Time'];

    const fetchLastSync = async () => {
        const res = await sqllastFetchData();
        if (res?.success && res?.statusCode === 200) {
            const date = res.data?.End_Time;
            setLastSync(date);
        }
    };

    const formatLastSyncDate = (isoDate: any) => {
        if (!isoDate) return 'Loading...';
        const date = new Date(isoDate);
        const weekday = date.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' });
        const day = date.toLocaleString('en-US', { day: '2-digit', timeZone: 'UTC' });
        const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
        const year = date.toLocaleString('en-US', { year: 'numeric', timeZone: 'UTC' });
        return `${weekday}, ${day} ${month} ${year}(UTC)`;
    };

    const formattedLastSync = useMemo(() => formatLastSyncDate(lastSync), [lastSync]);

    const totalSqlFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            totalSqlType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await totalSqlData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTotalSql(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            totalSqlType: false
        }));
    };
    const totalCostFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            totalCostType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await totalCostData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTotalCost(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            totalCostType: false
        }));
    };

    const newWarehouseFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            newWarehouseType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await newWarehouseData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success && resp.data) {
            setTotalNewWarehouse(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            newWarehouseType: false
        }));
    };

    const costTrendFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            costTrendType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await costTrendData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setCostTrend(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            costTrendType: false
        }));
    };

    const topDBUFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            topDBUType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await topDBUData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTopDBU(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            topDBUType: false
        }));
    };

    const topCostFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            topCostType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await topCostData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTopCost(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            topCostType: false
        }));
    };

    const longRunFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({ ...prev, longRunType: true }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];
        const resp = await lonRunData({
            startDate: startFormatted,
            endDate: endFormatted,
            env,
            sector,
            location,
            selectedDateInString
        });
        if (resp?.success) {
            const docs = resp.data ?? [];
            const filteredDocs = filterTableData(docs);
            setLongRun(filteredDocs);
            setColumns(generateFixedColumns());
        }

        setLoader((prev) => ({ ...prev, longRunType: false }));
    };

    // Calculate pagination data from frontend
    const paginationData = useMemo(() => {
        const totalElements = longRun.length;
        const totalPages = Math.ceil(totalElements / pageSize);
        return { totalElements, totalPages };
    }, [longRun, pageSize]);

    // Slice data for current page
    const paginatedLongRun = useMemo(() => {
        const startIdx = (page - 1) * pageSize;
        return longRun.slice(startIdx, startIdx + pageSize);
    }, [longRun, page, pageSize]);

    const handlePageChange = (event: any, newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (event: SelectChangeEvent<string>) => {
        setPageSize(Number(event.target.value));
        setPage(1); // Reset to first page when page size changes
    };

    const handleExportRequest = async () => {
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];
        await lonRunData({
            startDate: startFormatted,
            endDate: endFormatted,
            env,
            sector,
            location,
            selectedDateInString,
            page,
            limit: pageSize,
            exportType: 'excel'
        });
    };

    const failedMetricsFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            failedMetricsType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await failedMetricsData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setFailedMetrics(resp?.data[0]);
        }
        setLoader((prev) => ({
            ...prev,
            failedMetricsType: false
        }));
    };

    const peakCountFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            peakCountType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await peakCountData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setPeakCount(resp?.data[0]);
        }
        setLoader((prev) => ({
            ...prev,
            peakCountType: false
        }));
    };

    const queryTrendFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            queryTrendType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await queryTrendData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setQueryTrend(resp.data);
        }
        setLoader((prev) => ({
            ...prev,
            queryTrendType: false
        }));
    };

    const topGroupDbuFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            topGroupDbuType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await topGroupDbuData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTopGroupDbu(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            topGroupDbuType: false
        }));
    };

    const topGroupCostFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            topGroupCostType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await topGroupCostData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTopGroupCost(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            topGroupCostType: false
        }));
    };

    const topUsageCatalogsFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            topUsageCatalogsData: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await topUsageCatalogsData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTopUsageCatalogs(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            topUsageCatalogsData: false
        }));
    };

    const topUsageCatalogsCost = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            topUsageLogsCost: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await topUsageCost(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTopUsageCatalogsCst(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            topUsageLogsCost: false
        }));
    };

    const colorMap: Record<string, string> = { PRO: '#01C008', CLASSIC: '#FCC046', SERVERLESS: '#FF0000' };
    const chartData = totalSql.map(item => ({ value: item.total_warehouses_sum, name: item._id.charAt(0) + item._id.slice(1).toLowerCase(), itemStyle: { color: colorMap[item._id] } }));
    const totalSum = totalSql.reduce((acc, item) => acc + item.total_warehouses_sum, 0);

    const WarehousesChart: EChartsOption = {
        tooltip: {
            backgroundColor: "#FFFFFF",
            trigger: "item",
            textStyle: { color: "#000000" },
        },
        legend: createLegendConfig(isSmallScreen, colorMap),
        grid: { bottom: "10%" },
        color: ["#01C008", "#FCC046", "#FF0000"],
        series: [
            {
                name: "Warehouses",
                type: "pie",
                radius: chartRadius,
                center: ["50%", "48%"],
                data: chartData,
                avoidLabelOverlap: false,
                label: createSeriesLabel(isSmallScreen, (v: number) => `${compactFormatter.format(v)}`),
                emphasis: {
                    scale: true,
                    scaleSize: 12,
                    itemStyle: {
                        shadowBlur: 12,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.4)",
                    },
                },
            }
        ],
    };
    const orderCost = ['PRO', 'CLASSIC', 'SERVERLESS'];
    const costData = orderCost.map(key => {
        const item = totalCost.find(i => i._id === key);
        return {
            value: item?.total_cost ?? 0,
            name: key.charAt(0) + key.slice(1).toLowerCase(), // Title Case
            itemStyle: { color: colorMap[key] }
        };
    });
    const totalCostSum = totalCost.reduce((acc, item) => acc + item.total_cost, 0);
    const formattedTotal = formatter.format(totalCostSum);
    const costCategory: EChartsOption = {
        tooltip: {
            backgroundColor: "#FFFFFF",
            trigger: "item",
            textStyle: { color: "#000000" },
        },
        legend: createLegendConfig(isSmallScreen, costData.map(item => item.name)),
        grid: { bottom: "10%" },
        color: ["#01C008", "#FCC046", "#FF0000"],
        series: [
            {
                name: "Warehouses",
                type: "pie",
                radius: chartRadius,
                center: ["50%", "48%"],
                data: costData,
                avoidLabelOverlap: false,
                label: createSeriesLabel(isSmallScreen, (v: number) => `${formatter.format(v)}`),
                emphasis: {
                    scale: true,
                    scaleSize: 12,
                    itemStyle: {
                        shadowBlur: 12,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.4)",
                    },
                },
            }
        ],
    };


    const order = ['PRO', 'CLASSIC', 'SERVERLESS'];
    const sortedData = [...totalNewWarehouse].sort((a, b) => order.indexOf(a._id) - order.indexOf(b._id));
    const seriesData = sortedData.map((item) => ({
        name: item._id.charAt(0) + item._id.slice(1).toLowerCase(),
        value: item.total_warehouses_new,
        itemStyle: { color: colorMap[item._id] }
    }));
    const totalWarehouses = seriesData.reduce((sum, item) => sum + item.value, 0);

    const WarehousesByCategory: EChartsOption = {
        tooltip: {
            backgroundColor: "#FFFFFF",
            trigger: "item",
            textStyle: { color: "#000000" },
            formatter: (params: any) => {
                return `${params.name}: ${compactFormatter.format(params.value)}`;
            },
        },
        graphic: {
            type: 'text',
            left: '25%',
            top: '46%',
            style: {
                text: `Total: ${compactFormatter.format(totalWarehouses || 0)}`,
                fill: '#000000',
                font: isSmallScreen ? 'bold 14px Montserrat, sans-serif' : 'bold 18px Montserrat, sans-serif',
                align: 'center',
                verticalAlign: 'middle',
            }
        },
        legend: {
            ...createLegendConfig(isSmallScreen, seriesData.map(item => item.name)),
            orient: 'vertical',
            right: '3%',
            top: 'center',
            left: 'auto',
        },
        grid: { bottom: "10%" },
        color: ["#01C008", "#FCC046", "#FF0000"],
        series: [
            {
                name: "Warehouses",
                type: "pie",
                radius: isSmallScreen ? ['40%', '60%'] : ['48%', '68%'],
                //center: ["50%", "46%"],
                center: ["35%", "46%"],
                data: seriesData,
                avoidLabelOverlap: false,
                label: createSeriesLabel(isSmallScreen, (v: number) => `${compactFormatter.format(v)}`),
                emphasis: {
                    scale: true,
                    scaleSize: 12,
                    itemStyle: {
                        shadowBlur: 12,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.4)",
                    },
                },
            }
        ]
    };

    const desiredOrder = ['PRO', 'CLASSIC', 'SERVERLESS'];
    const dates = Array.from(new Set(costTrend.map(item => item.date)));
    const warehouseTypes = Array.from(new Set(costTrend.map(item => item.warehouse_type)));
    const warehouseTypesSorted = desiredOrder.filter(type =>
        warehouseTypes.some(wt => wt.toUpperCase() === type)
    );

    const warehouseSeriesData: SeriesOption[] =
        warehouseTypesSorted.map(type => {
            const data = dates.map(date => {
                const entry = costTrend.find(
                    item => item.date === date && item.warehouse_type === type
                );
                return entry ? entry.total_cost : 0;
            });

            return {
                name: type,
                type: "line",
                data,
                smooth: true,
                symbol: "none",
                symbolSize: 6,
                itemStyle: { color: colorMap[type] },
                lineStyle: { color: colorMap[type] }
            };
        })


    const WarehouseCostTrend: EChartsOption = {
        backgroundColor: "#FFFFFF",
        tooltip: {
            backgroundColor: "#FFFFFF",
            textStyle: { color: "#000000" },
            trigger: "axis",
            formatter: (params: any) => {
                let tooltip = `${params[0].axisValue}<br/>`;
                params.forEach((p: any) => {
                    tooltip += `${p.marker} ${toTitleCase(p.seriesName)}: ${formatter.format(p.value)}<br/>`;
                });
                return tooltip;
            }
        },
        color: ['#01C008', '#FCC046', '#FF0000'],
        legend: {
            data: warehouseTypesSorted,
            orient: 'horizontal',
            left: '2%',
            bottom: '1%',
            icon: 'roundRect',
            formatter: (name: string) => toTitleCase(name),
            textStyle: { color: "#000000", fontSize: 15 }
        },
        grid: {
            left: 50,
            right: 20,
            top: 50,
            bottom: 60
        },
        xAxis: {
            type: "category",
            data: dates,
            axisLine: { lineStyle: { color: "#71758FCC" } },
            axisLabel: { color: "#000000" },
        },
        yAxis: {
            type: "value",
            splitLine: {
                show: false,
            },
            axisLine: { show: true, lineStyle: { color: "#71758FCC" } },
            axisLabel: {
                color: "#000000",
                formatter: (value: number) => `$${compactFormatter.format(value)}`
            },
        },
        series: warehouseSeriesData
    };
    const allowedTypes: Set<string> = new Set(["PRO", "CLASSIC", "SERVERLESS"]);
    const queryDates = Array.from(new Set(queryTrend.map(item => item.date)));
    const volumeTypes = Array.from(new Set(queryTrend.map(item => item.warehouse_type))).filter((type) => allowedTypes.has(type));
    const volumeData: SeriesOption[] = volumeTypes.map(type => {
        const data = queryDates.map(date => {
            const entry = queryTrend.find(
                item => item.date === date && item.warehouse_type === type
            );
            return entry ? entry.queries_count : 0;
        });
        return {
            name: type,
            type: "line",
            data,
            smooth: true,
            symbol: "none",
            symbolSize: 6,
            itemStyle: { color: colorMap[type] },
            lineStyle: { color: colorMap[type] }
        };
    });

    const QueryVolumeTrend: EChartsOption = {
        backgroundColor: '#FFFFFF',
        tooltip: {
            backgroundColor: "#FFFFFF",
            textStyle: { color: "#000000" },
            trigger: "axis",
            formatter: (params: any) => {
                let tooltip = `${params[0].axisValue}<br/>`;
                params.forEach((p: any) => {
                    tooltip += `${p.marker}  ${toTitleCase(p.seriesName)}: ${compactFormatter.format(p.value)}<br/>`;
                });
                return tooltip;
            }
        },
        color: ['#01C008', '#FCC046', '#FF0000'],
        legend: {
            data: volumeTypes,
            orient: 'horizontal',
            left: '2%',
            bottom: '1%',
            icon: 'roundRect',
            formatter: (name: string) => toTitleCase(name),
            textStyle: { color: "#000000", fontSize: 15 }
        },
        grid: {
            left: 50,
            right: 20,
            top: 60,
            bottom: 60
        },
        xAxis: {
            type: 'category',
            data: queryDates,
            axisLine: {
                lineStyle: { color: '#71758FCC' }
            },
            axisLabel: {
                color: '#000000'
            },
        },
        yAxis: {
            type: 'value',
            splitLine: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: { color: '#71758FCC' }
            },
            axisLabel: {
                color: '#000000',
                formatter: (value: number) => `${compactFormatter.format(value)}`
            }
        },
        series: volumeData
    };

    const sortedDBU = [...topDBU].sort((a, b) => b.total_dbus - a.total_dbus);
    const yAxisLabels = sortedDBU.map(item => item.workspace_name).reverse();
    const dbuValues = sortedDBU.map(item => item.total_dbus).reverse();

    const TopDBU: EChartsOption = {
        backgroundColor: '#FFFFFF',
        tooltip: {
            backgroundColor: '#FFFFFF',
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: {
                color: '#000000'
            },
            formatter: (params: any) => {
                const value = params[0].value;
                return `${params[0].name}<br/>DBUs: ${compactFormatter.format(value)}`;
            }
        },
        grid: {
            left: '10%',
            right: '5%',
            bottom: '5%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisLabel: {
                show: true,
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return (value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return (value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return (value / 1_000) + 'K';
                    } else {
                        return value.toString();
                    }
                },

                fontSize: 10,
                color: '#000000'
            },
            axisLine: {
                show: true
            },
            axisTick: {
                show: true
            },
            splitLine: {
                show: false,
                lineStyle: {
                    color: '#1c3b6b'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: yAxisLabels,
            axisLabel: {
                color: '#000000',
                formatter: function (value: any) {
                    return value.length > 10 ? value.substring(0, 10) + '…' : value;
                }
            }
        },
        series: [
            {
                name: 'Spend',
                type: 'bar',
                data: dbuValues,
                itemStyle: {
                    color: '#009BD8'
                },
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'inside',
                    color: '#000000',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${compactFormatter.format(value)}`;
                    }
                }
            }
        ]
    };

    const sortedCosts = [...topCost].sort((a, b) => b.total_costs - a.total_costs).slice(0, 10);
    const yAxisLabel = sortedCosts.map(item => item.workspace_name).reverse();
    const costValues = sortedCosts.map(item => item.total_costs).reverse();

    const TopCost: EChartsOption = {
        backgroundColor: '#FFFFFF',
        tooltip: {
            backgroundColor: '#FFFFFF',
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: {
                color: '#000000'
            },
            formatter: (params: any) => {
                const value = params[0].value;
                return `${params[0].name}<br/>Cost: ${formatter.format(value)}`;
            }
        },
        grid: {
            left: '10%',
            right: '5%',
            bottom: '5%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisLabel: {
                show: true,
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return '$' + (value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return '$' + (value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return '$' + (value / 1_000) + 'K';
                    } else {
                        return '$' + value;
                    }
                },
                fontSize: 10,
                color: '#000000'
            },
            axisLine: {
                show: true
            },
            axisTick: {
                show: true
            },
            splitLine: {
                show: false,
                lineStyle: {
                    color: '#1c3b6b'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: yAxisLabel,
            axisLabel: {
                color: '#000000',
                formatter: function (value: any) {
                    return value.length > 10 ? value.substring(0, 10) + '…' : value;
                }
            }
        },
        series: [
            {
                name: 'Spend',
                type: 'bar',
                data: costValues,
                itemStyle: {
                    color: '#009BD8'
                },
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'inside',
                    color: '#000000',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${formatter.format(value)}`;
                    }
                }
            }
        ]
    };

    const sortedCosts1 = [...topGroupDbu].sort((a, b) => b.total_dbus - a.total_dbus).slice(0, 10);
    const yAxisLabel1 = sortedCosts1.map(item => item.executed_by).reverse();
    const costValues1 = sortedCosts1.map(item => item.total_dbus).reverse();

    const TopCost1: EChartsOption = {
        backgroundColor: '#FFFFFF',
        tooltip: {
            backgroundColor: '#FFFFFF',
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: {
                color: '#000000'
            },
            formatter: (params: any) => {
                const value = params[0].value;
                return `${params[0].name}<br/>DBU Usages: ${compactFormatter.format(value)}`;
            }
        },
        grid: {
            left: '10%',
            right: '5%',
            bottom: '5%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisLabel: {
                show: true,
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return (value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return (value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return (value / 1_000) + 'K';
                    } else {
                        return value.toString();
                    }
                },
                fontSize: 10,
                color: '#000000'
            },
            axisLine: {
                show: true
            },
            axisTick: {
                show: true
            },
            splitLine: {
                show: false,
                lineStyle: {
                    color: '#1c3b6b'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: yAxisLabel1,
            axisLabel: {
                color: '#000000',
                formatter: function (value: any) {
                    return value.length > 10 ? value.substring(0, 10) + '…' : value;
                }
            }
        },
        series: [
            {
                name: 'Spend',
                type: 'bar',
                data: costValues1,
                itemStyle: {
                    color: '#009BD8'
                },
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'inside',
                    color: '#000000',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${compactFormatter.format(value)}`;
                    }
                },
            }
        ]
    };

    const sortedCosts2 = [...topGroupCost].sort((a, b) => b.total_cost_usd - a.total_cost_usd).slice(0, 10);
    const yAxisLabel2 = sortedCosts2.map(item => item.executed_by).reverse();
    const costValues2 = sortedCosts2.map(item => item.total_cost_usd).reverse();

    const TopCost2: EChartsOption = {
        backgroundColor: '#FFFFFF',
        tooltip: {
            backgroundColor: '#FFFFFF',
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: {
                color: '#000000'
            },
            formatter: (params: any) => {
                const value = params[0].value;
                return `${params[0].name}<br/>Cost: ${formatter.format(value)}`;
            }
        },
        grid: {
            left: '10%',
            right: '5%',
            bottom: '5%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisLabel: {
                show: true,
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return '$' + (value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return '$' + (value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return '$' + (value / 1_000) + 'K';
                    } else {
                        return '$' + value;
                    }
                },
                fontSize: 10,
                color: '#000000'
            },
            axisLine: {
                show: true
            },
            axisTick: {
                show: true
            },
            splitLine: {
                show: false,
                lineStyle: {
                    color: '#1c3b6b'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: yAxisLabel2,
            axisLabel: {
                color: '#000000',
                formatter: function (value: any) {
                    return value.length > 10 ? value.substring(0, 10) + '…' : value;
                }
            }
        },
        series: [
            {
                name: 'Spend',
                type: 'bar',
                data: costValues2,
                itemStyle: {
                    color: '#009BD8'
                },
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'inside',
                    color: '#000000',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${formatter.format(value)}`;
                    }
                }
            }
        ]
    };

    const sortedCosts3 = [...topUsageCatalogs].sort((a, b) => b.total_usage_count - a.total_usage_count).slice(0, 10);
    const yAxisLabel3 = sortedCosts3.map(item => item.usage_by).reverse();
    const costValues3 = sortedCosts3.map(item => item.total_usage_count).reverse();

    const TopCost3: EChartsOption = {
        backgroundColor: '#FFFFFF',
        tooltip: {
            backgroundColor: '#FFFFFF',
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: {
                color: '#000000'
            },
            formatter: (params: any) => {
                const value = params[0].value;
                return `${params[0].name}<br/>Usage Catalogs: ${compactFormatter.format(value)}`;
            }
        },
        grid: {
            left: '10%',
            right: '5%',
            bottom: '5%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisLabel: {
                show: true,
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return (value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return (value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return (value / 1_000) + 'K';
                    } else {
                        return value.toString();
                    }
                },
                fontSize: 10,
                color: '#000000'
            },
            axisLine: {
                show: true
            },
            axisTick: {
                show: true
            },
            splitLine: {
                show: false,
                lineStyle: {
                    color: '#1c3b6b'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: yAxisLabel3,
            axisLabel: {
                color: '#000000',
                formatter: function (value: any) {
                    return value.length > 10 ? value.substring(0, 10) + '…' : value;
                }
            }
        },
        series: [
            {
                name: 'Spend',
                type: 'bar',
                data: costValues3,
                itemStyle: {
                    color: '#009BD8'
                },
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'inside',
                    color: '#000000',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${compactFormatter.format(value)}`;
                    }
                }
            }
        ]
    };

    const sortedCatalogCosts3 = [...topUsageCatalogsCst].sort((a, b) => b.total_catalog_cost_usd - a.total_catalog_cost_usd).slice(0, 10);
    const yAxisLabelCatalogCost = sortedCatalogCosts3.map(item => item.usage_by).reverse();
    const costValuesCatalogCost = sortedCatalogCosts3.map(item => item.total_catalog_cost_usd).reverse();

    const TopUsageCatalogCost: EChartsOption = {
        backgroundColor: '#FFFFFF',
        tooltip: {
            backgroundColor: '#FFFFFF',
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: {
                color: '#000000'
            },
            formatter: (params: any) => {
                const value = params[0].value;
                return `${params[0].name}<br/>Usage Catalogs: ${formatter.format(value)}`;
            }
        },
        grid: {
            left: '10%',
            right: '5%',
            bottom: '5%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisLabel: {
                show: true,
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return (value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return (value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return (value / 1_000) + 'K';
                    } else {
                        return value.toString();
                    }
                },
                fontSize: 10,
                color: '#000000'
            },
            axisLine: {
                show: true
            },
            axisTick: {
                show: true
            },
            splitLine: {
                show: false,
                lineStyle: {
                    color: '#1c3b6b'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: yAxisLabelCatalogCost,
            axisLabel: {
                color: '#000000',
                formatter: function (value: any) {
                    return value.length > 10 ? value.substring(0, 10) + '…' : value;
                }
            }
        },
        series: [
            {
                name: 'Spend',
                type: 'bar',
                data: costValuesCatalogCost,
                itemStyle: {
                    color: '#009BD8'
                },
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'inside',
                    color: '#000000',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${formatter.format(value)}`;
                    }
                }
            }
        ]
    };

    // refs to help with env fallback logic
    const initialEnvAppliedRef = useRef(false);
    const envRef = useRef(env);
    useEffect(() => { envRef.current = env; }, [env]);

    const getEnvFetch = async (location: string) => {
        // preserve incoming navigation state env when available (apply once)
        try {
            setLoader((prev) => ({
                ...prev,
                env: true
            }));
            const res = await workspaceGetEnv(location);
            const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
            setEnvOptions(options);
            // if navigation provided an env, apply it once; otherwise ensure current env is valid
            if (!initialEnvAppliedRef.current && navigationState?.env) {
                setEnv(navigationState.env);
                initialEnvAppliedRef.current = true;
            } else {
                const availableValues = options.map((o) => o.value);
                const current = envRef.current;
                if (!availableValues.includes(current)) {
                    const fallback = availableValues.includes('both') ? 'both' : (availableValues[0] || 'both');
                    setEnv(fallback);
                }
            }
        } catch (error) {
            console.error('Failed to fetch environment options:', error);
        } finally {
            setLoader((prev) => ({
                ...prev,
                env: false
            }));
        }
    };

    const updateHistoryState = (overrides: Partial<{
        startDate: string;
        endDate: string;
        env: string;
        sector: string;
        location: string;
        selectedDateInString: string;
    }>) => {
        const newState = {
            startDate,
            endDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString,
            ...overrides
        };
        navigate(routerLocation.pathname, { state: newState, replace: true });
    };

    const handleWidgetOnClick = (chartType: any) => {
        widgetOnClickHandler(chartType, {
            navigate,
            startDate,
            endDate,
            env,
            sectorName,
            location,
            selectedDateInString,
            PRESERVE_FILTER_KEY
        });
    };

    const handleTotalCountByCategoryClick = () => {
        navigate(`/databricksADB/performance-dashboard`, {
            state: {
                startDate,
                endDate,
                clickedStartDate: null,
                clickedEndDate: null,
                env,
                sector: sectorName,
                location,
                selectedDateInString,
                searchSource: 'totalCountByCategory',
                searchPoint: null,
                detailedTableTitle: 'Total Count By Category',
                detailedTableType: 'count-by-category-detail',
                pageHeader: 'DBSQL Performance',
                pageSubHeader: 'Total Count By Category',
                workspaceName: null,
                warehouseType: null,
                warehouseName: null,
                userExecutedBy: null,
                catalogName: null,
                filters: JSON.stringify([]),
            },
        });
    };

    const handleTotalCostByCategoryClick = () => {
        navigate(`/databricksADB/performance-dashboard`, {
            state: {
                startDate,
                endDate,
                clickedStartDate: null,
                clickedEndDate: null,
                env,
                sector: sectorName,
                location,
                selectedDateInString,
                searchSource: 'totalCostByCategory',
                searchPoint: null,
                detailedTableTitle: 'Total Cost By Category',
                detailedTableType: 'cost-by-category-detail',
                pageHeader: 'DBSQL Performance',
                pageSubHeader: 'Total Cost By Category',
                workspaceName: null,
                warehouseType: null,
                warehouseName: null,
                userExecutedBy: null,
                catalogName: null,
                filters: JSON.stringify([]),
            },
        });
    };

    const handleNewWarehouseByCategoryClick = () => {
        navigate(`/databricksADB/performance-dashboard`, {
            state: {
                startDate,
                endDate,
                clickedStartDate: null,
                clickedEndDate: null,
                env,
                sector: sectorName,
                location,
                selectedDateInString,
                searchSource: 'warehouseByCategory',
                searchPoint: null,
                detailedTableTitle: 'Warehouse By Category',
                detailedTableType: 'warehouse-by-category-detail',
                pageHeader: 'DBSQL Performance',
                pageSubHeader: 'Warehouse By Category',
                workspaceName: null,
                warehouseType: null,
                warehouseName: null,
                userExecutedBy: null,
                catalogName: null,
                filters: JSON.stringify([]),
            },
        });
    };

    const handleTotalCountChartClick = (params: any) => handleChartOnClick(params, 'totalCountByCategory');

    const handleChartOnClick = (params: any, chartType: any) => {
        const defaultResult: any = {
            detailedTableTitle: 'Total Count By Category',
            detailedTableType: 'count-by-category-detail',
            pageSubHeader: 'Total Count By Category',
            clickedStartDate: null,
            clickedEndDate: null,
            clickedWorkspaceName: null,
            warehouseTypeFilter: null,
            warehouseNameFilter: null,
            userExecutedByFilter: null,
            catalogNameFilter: null,
            warehouseNameOptions: [] as string[],
            userExecutedByOptions: [] as string[],
            catalogNameFilterOptions: [] as string[]
        };

        const dateRangeStr = (s: any, e: any) => `(${s} - ${e})`;

        const handlers: Record<string, (p: any) => Partial<typeof defaultResult>> = {
            totalCountByCategory: (p) => ({
                detailedTableTitle: `Total Count By Category - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                pageSubHeader: `Total Count By Category - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                warehouseTypeFilter: p.name?.toUpperCase()
            }),
            totalCostByCategory: (p) => ({
                detailedTableTitle: `Total Cost By Category - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                detailedTableType: 'cost-by-category-detail',
                pageSubHeader: `Total Cost By Category - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                warehouseTypeFilter: p.name?.toUpperCase()
            }),
            warehouseByCategory: (p) => ({
                detailedTableTitle: `Warehouse By Category - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                detailedTableType: 'warehouse-by-category-detail',
                pageSubHeader: `Warehouse By Category - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                warehouseTypeFilter: p.name?.toUpperCase()
            }),
            warehouseCostTrend: (p) => {
                const monthRange = getMonthRange(p?.name || '');
                const typeStr = p.seriesName ? ` - ${p.seriesName}` : '';
                return {
                    clickedStartDate: monthRange.startDate,
                    clickedEndDate: monthRange.endDate,
                    detailedTableTitle: `Warehouse Cost Trend${typeStr} ${dateRangeStr(monthRange.startDate, monthRange.endDate)}`,
                    detailedTableType: 'warehouse-cost-trend-detail',
                    pageSubHeader: `Warehouse Cost Trend${typeStr} ${dateRangeStr(monthRange.startDate, monthRange.endDate)}`,
                    warehouseTypeFilter: p.seriesName ? p.seriesName.toUpperCase() : null
                };
            },
            queryVolumeTrend: (p) => {
                const monthRange = getMonthRange(p?.name || '');
                const typeStr = p.seriesName ? ` - ${p.seriesName}` : '';
                return {
                    clickedStartDate: monthRange.startDate,
                    clickedEndDate: monthRange.endDate,
                    detailedTableTitle: `Query Volume Trend${typeStr} ${dateRangeStr(monthRange.startDate, monthRange.endDate)}`,
                    detailedTableType: 'query-volume-trend-detail',
                    pageSubHeader: `Query Volume Trend${typeStr} ${dateRangeStr(monthRange.startDate, monthRange.endDate)}`,
                    warehouseTypeFilter: p.seriesName ? p.seriesName.toUpperCase() : null
                };
            },
            dbuUsageSqlWarehouses: (p) => ({
                detailedTableTitle: `DBU Usage By SQL Warehouses - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                detailedTableType: 'dbu-usage-sql-warehouses-detail',
                pageSubHeader: `DBU Usage By SQL Warehouses - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                warehouseNameFilter: p.name,
                warehouseNameOptions: topDBU.map((item: any) => item.workspace_name)
            }),
            costSqlWarehouse: (p) => ({
                detailedTableTitle: `Cost By SQL Warehouse - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                detailedTableType: 'cost-by-sql-warehouses-detail',
                pageSubHeader: `Cost By SQL Warehouse - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                warehouseNameFilter: p.name,
                warehouseNameOptions: topCost.map((item: any) => item.workspace_name)
            }),
            userByDbuUsage: (p) => ({
                detailedTableTitle: `User By DBU Usage - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                detailedTableType: 'user-by-dbu-usage-detail',
                pageSubHeader: `User By DBU Usage - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                userExecutedByFilter: p.name,
                userExecutedByOptions: TopCost1?.series?.[0]?.data?.map((_: any, index: number) => sortedCosts1?.[index]?.executed_by) || []
            }),
            userByCost: (p) => ({
                detailedTableTitle: `User By Cost - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                detailedTableType: 'user-by-cost-detail',
                pageSubHeader: `User By Cost - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                userExecutedByFilter: p.name,
                userExecutedByOptions: TopCost2?.series?.[0]?.data?.map((_: any, index: number) => sortedCosts1?.[index]?.executed_by) || []
            }),
            catalogsCount: (p) => ({
                detailedTableTitle: `Catalogs Count - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                detailedTableType: 'catalogs-count-detail',
                pageSubHeader: `Catalogs Count - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                catalogNameFilter: p.name,
                catalogNameFilterOptions: TopCost3?.series?.[0]?.data?.map((_: any, index: number) => sortedCosts3?.[index]?.usage_by) || []
            }),
            catalogsCost: (p) => ({
                detailedTableTitle: `Catalogs Cost - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                detailedTableType: 'catalogs-cost-detail',
                pageSubHeader: `Catalogs Cost - ${p.name} ${dateRangeStr(startDate, endDate)}`,
                catalogNameFilter: p.name,
                catalogNameFilterOptions: TopCost3?.series?.[0]?.data?.map((_: any, index: number) => sortedCosts3?.[index]?.usage_by) || []
            })
        };

        const result = { ...defaultResult, ...(handlers[chartType] ? handlers[chartType](params) : {}) };

        const buildFilters = (r: typeof result) => {
            if (r.warehouseTypeFilter && ['PRO', 'CLASSIC', 'SERVERLESS'].includes(r.warehouseTypeFilter)) {
                return [{ operator: 'AND', field: 'warehouse_type', value: r.warehouseTypeFilter }];
            }
            if (r.catalogNameFilter) {
                return [{ operator: 'AND', field: 'catalog_name', value: r.catalogNameFilter }];
            }
            if (r.warehouseNameFilter) {
                return [{ operator: 'AND', field: 'warehouse_name', value: r.warehouseNameFilter }];
            }
            if (r.userExecutedByFilter) {
                return [{ operator: 'AND', field: 'executed_by', value: r.userExecutedByFilter }];
            }
            return [];
        };

        const filtersArray = buildFilters(result);

        localStorage.removeItem(result.detailedTableType);
        const snap = { startDate, endDate, selectedDateInString };
        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));

        navigate('/databricksADB/performance-dashboard', {
            state: {
                startDate,
                endDate,
                clickedStartDate: result.clickedStartDate,
                clickedEndDate: result.clickedEndDate,
                env,
                sector: sectorName,
                location,
                selectedDateInString,
                searchSource: chartType,
                searchPoint: { seriesName: params.seriesName, name: params.name, value: params.value },
                detailedTableTitle: result.detailedTableTitle,
                detailedTableType: result.detailedTableType,
                pageHeader: 'DBSQL Performance',
                pageSubHeader: result.pageSubHeader,
                workspaceName: result.clickedWorkspaceName,
                warehouseType: result.warehouseTypeFilter,
                warehouseName: result.warehouseNameFilter,
                userExecutedBy: result.userExecutedByFilter,
                catalogName: result.catalogNameFilter,
                clickedItemName: params.name,
                filters: JSON.stringify(filtersArray),
                warehouseNameOptions: result.warehouseNameOptions,
                userExecutedByOptions: result.userExecutedByOptions,
                catalogNameFilterOptions: result.catalogNameFilterOptions
            }
        });

    }

    // Update filters when navigation state changes
    const applyNavigationDateFilters = (navigationState: typeof navState) => {
        if (navigationState.selectedDateInString?.trim() !== 'custom') {
            if (navigationState.startDate) setStartDate(navigationState.startDate);
            if (navigationState.endDate) setEndDate(navigationState.endDate);
            if (navigationState.selectedDateInString) setSelectedDateInString(navigationState.selectedDateInString);
        }
    };

    const applyPreservedFilters = (preservedRaw: string | null, navigationState: typeof navState) => {
        if (preservedRaw && (navigationState.selectedDateInString === 'customDateRange')) {
            const p = JSON.parse(preservedRaw);
            if (p.startDate) setStartDate(p.startDate);
            if (p.endDate) setEndDate(p.endDate);
            if (p.selectedDateInString) setSelectedDateInString(p.selectedDateInString);
            restoredRef.current = true;
        }
    };

    const applyNavigationStateFilters = (navigationState: typeof navState) => {
        if (navigationState.env) setEnv(navigationState.env);
        if (navigationState.sector) { setSector(navigationState.sector); setSectorName(navigationState.sector); }
        if (navigationState.location) setLocation(navigationState.location);
    };

    useEffect(() => {
        const preservedRaw = (() => { return sessionStorage.getItem(PRESERVE_FILTER_KEY); })();

        if (navigationState) {
            applyNavigationDateFilters(navigationState);
            applyPreservedFilters(preservedRaw, navigationState);
            applyNavigationStateFilters(navigationState);
            initialEnvAppliedRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!(startDate && endDate && env && sector && location && selectedDateInString)) return;

        let mounted = true;
        const runAll = async () => {
            try {
                await Promise.all([
                    // adb_workspace_metrics
                    totalSqlFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    totalCostFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    newWarehouseFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    costTrendFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    topDBUFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    topCostFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    failedMetricsFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    peakCountFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    queryTrendFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    topGroupDbuFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    topGroupCostFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    topUsageCatalogsFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    topUsageCatalogsCost(startDate, endDate, env, sector, location, selectedDateInString),
                    fetchLastSync()
                ]);
            } catch (err) {
                console.error('Workspace fetches failed', err);
            } finally {
                if (mounted && restoredRef.current) {
                    sessionStorage.removeItem(PRESERVE_FILTER_KEY);
                    restoredRef.current = false;
                }
            }
        };

        runAll();
        return () => { mounted = false; };
    }, [startDate, endDate, env, sector, location, selectedDateInString]);

    useEffect(() => {
        longRunFetch(startDate, endDate, env, sector, location, selectedDateInString);
    }, [startDate, endDate, env, sector, location, selectedDateInString])

    useEffect(() => {
        getEnvFetch(location);
    }, [location]);

    const chartWidgetsConfig = [
        {
            id: 'Warehouse-count',
            md: 4,
            loaderKey: 'totalSqlType' as keyof typeof loader,
            title: 'Total Count By Category',
            total: totalSum,
            chartOption: WarehousesChart,
            onTotalClick: handleTotalCountByCategoryClick,
            onChartClick: handleTotalCountChartClick,
        },
        {
            id: 'cost-category',
            md: 4,
            loaderKey: 'totalCostType' as keyof typeof loader,
            title: 'Total Cost By Category',
            total: formattedTotal,
            chartOption: costCategory,
            onTotalClick: handleTotalCostByCategoryClick,
            onChartClick: (params: any) => handleChartOnClick(params, 'totalCostByCategory'),
        },
    ];

    const secondaryChartsConfig = [
        {
            id: 'warehouse-trend',
            md: 6,
            loaderKey: 'costTrendType' as keyof typeof loader,
            title: 'Warehouse Cost Trend',
            option: WarehouseCostTrend,
            chartType: 'warehouseCostTrend',
        },
        {
            id: 'volume-trend',
            md: 6,
            loaderKey: 'queryTrendType' as keyof typeof loader,
            title: 'Query Volume Trend',
            option: QueryVolumeTrend,
            chartType: 'queryVolumeTrend',
        },
    ];

    const topTenChartsConfig = [
        {
            id: 'dbu-warehouse',
            md: 4,
            loaderKey: 'topDBUType' as keyof typeof loader,
            title: 'Top 10 DBU Usage SQL Warehouses',
            option: TopDBU,
            chartType: 'dbuUsageSqlWarehouses',
        },
        {
            id: 'sql-warehouse',
            md: 4,
            loaderKey: 'topCostType' as keyof typeof loader,
            title: 'Top 10 Cost SQL Warehouse',
            option: TopCost,
            chartType: 'costSqlWarehouse',
        },
        {
            id: 'top-10-user-groups-by-dbu-usage',
            md: 4,
            loaderKey: 'topGroupDbuType' as keyof typeof loader,
            title: 'Top 10 User/Groups by DBU Usage',
            option: TopCost1,
            chartType: 'userByDbuUsage',
        },
        {
            id: 'top-10-user-groups-by-cost',
            md: 4,
            loaderKey: 'topGroupCostType' as keyof typeof loader,
            title: 'Top 10 User/Groups by Cost',
            option: TopCost2,
            chartType: 'userByCost',
        },
        {
            id: 'top-10-usage-catalogs',
            md: 4,
            loaderKey: 'topUsageCatalogsData' as keyof typeof loader,
            title: 'Top 10 Usage Catalogs Count',
            option: TopCost3,
            chartType: 'catalogsCount',
        },
        {
            id: 'top-10-usage-catalogs-cost',
            md: 4,
            loaderKey: 'topUsageLogsCost' as keyof typeof loader,
            title: 'Top 10 Usage Catalogs Cost',
            option: TopUsageCatalogCost,
            chartType: 'catalogsCost',
        },
    ];

    return (
        <PageWrapper>
            <Box bgcolor="backgroundLight.primary" sx={{ mt: { xs: '70px', sm: '60px', md: '10px' } }}>
                <PageHeader title="DBSQL Performance Dashboard" icon={DBsqlIcon} lastSync={formattedLastSync}>
                    <Box
                        sx={{
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <FiltersBar
                            location={location}
                            // pass the actual useState setters (not single-arg wrappers)
                            setLocation={setLocation}
                            env={env}
                            setEnv={setEnv}
                            envOptions={envOptions}
                            setEnvOptions={setEnvOptions}
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            handleDateSelect={handleDateSelect}
                            selectedDateInString={selectedDateInString}
                            setSelectedDateInString={setSelectedDateInString}
                            sectorName={sectorName}
                            // typed wrapper to handle SetStateAction<string> (function or value)
                            setSectorName={(v) => {
                                const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
                                setSectorName(resolved);
                                setSector(resolved);
                            }}
                            updateHistoryState={updateHistoryState}
                            hasYTD={true}
                            sectorOptionsList={sectorList}
                            searchSource={navigationState?.searchSource}
                        />
                    </Box>
                </PageHeader>
                <Box sx={{ p: { xs: 1, md: 3 }, width: '100%', overflow: 'hidden' }}>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        {/* Row 1: Dashboard Summary Widgets */}
                        {chartWidgetsConfig.map((config) => (
                            <DashboardCard key={config.id} id={config.id} md={config.md ?? 4} isLoading={loader[config.loaderKey]}>
                                <ChartWidgetWithTotal
                                    {...config}
                                    chartHeight="340px"
                                    isSmallScreen={isSmallScreen}
                                    isMediumScreen={isMediumScreen}
                                    showDivider={true}
                                />
                            </DashboardCard>
                        ))}
                        <DashboardCard key="warehouse-category" id="warehouse-category" md={4} isLoading={loader.newWarehouseType}>
                            <ChartSection
                                title="New Warehouse By Category"
                                option={WarehousesByCategory}
                                onClick={(params: any) => handleChartOnClick(params, 'warehouseByCategory')}
                            />
                        </DashboardCard>

                        {/* Row 2: Secondary Trend Charts */}
                        {secondaryChartsConfig.map((config) => (
                            <DashboardCard key={config.id} id={config.id} md={config.md} isLoading={loader[config.loaderKey]} height="310px">
                                <ChartSection
                                    title={config.title}
                                    option={config.option}
                                    onClick={(params: any) => handleChartOnClick(params, config.chartType)}
                                />
                            </DashboardCard>
                        ))}

                        {/* Row 3: Top 10 Charts (First half) */}
                        {topTenChartsConfig.slice(0, 2).map((config) => (
                            <DashboardCard key={config.id} id={config.id} isLoading={loader[config.loaderKey]} height="310px">
                                <ChartSection
                                    title={config.title}
                                    option={config.option}
                                    onClick={(params: any) => handleChartOnClick(params, config.chartType)}
                                />
                            </DashboardCard>
                        ))}

                        {/* Row 4: Long Running Queries Table */}
                        <Grid item xs={12} id="top-10-long-running-queries" sx={{ my: 1 }}>
                            <CustomizedDataGrid
                                title={<HighlightedText text="Top 50 Long Running Queries" />}
                                tableColumns={columns}
                                tableData={paginatedLongRun}
                                tableType="long-run"
                                defaultColumnNames={defaultColumns}
                                page={page}
                                pageSize={String(pageSize)}
                                paginationData={paginationData}
                                listLength={longRun.length}
                                handlepageSizeChange={handlePageSizeChange}
                                handlePageChange={handlePageChange}
                                isRenderPagination={true}
                                isDateRangeFilter={false}
                                isDisabled={false}
                                isFetching={loader.longRunType}
                                pageSizeOptions={pageSizeOptions}
                                showExport={true}
                                exportTableData={handleExportRequest}
                            />
                        </Grid>

                        {/* Row 5: Top 10 Charts (Remaining) */}
                        {topTenChartsConfig.slice(2).map((config) => (
                            <DashboardCard key={config.id} id={config.id} isLoading={loader[config.loaderKey]} height="310px">
                                <ChartSection
                                    title={config.title}
                                    option={config.option}
                                    onClick={(params: any) => handleChartOnClick(params, config.chartType)}
                                />
                            </DashboardCard>
                        ))}

                        {/* Row 6: Summary Metrics Number Widgets */}
                        <DashboardCard id="failed-aggregated" isLoading={loader.failedMetricsType} height="150px">
                            <NumberWidget
                                title={<HighlightedText text="Failed Queries" />}
                                number={failedMetrics ? `${failedMetrics.failurePercentage.toFixed(2)}%` : '0%'}
                                isLoading={loader.failedMetricsType}
                                handleEvent={() => handleWidgetOnClick('failedqueriesdetails')}
                                tooltipLabel=""
                                subText={
                                    failedMetrics
                                        ? `Associated cost: $${failedMetrics.total_failed_query_cost.toFixed(2)}`
                                        : 'Associated cost: $0.00'
                                }
                                bgColor='#ffffff'
                                color='#000000'
                                valueFontWeight={300}
                            />
                        </DashboardCard>
                        <DashboardCard id="peak-concurrency" isLoading={loader.peakCountType} height="150px">
                            <NumberWidget
                                title={<HighlightedText text="Peak Concurrency Query Count" />}
                                number={peakCount ? peakCount.peakCount.toLocaleString() : '0'}
                                isLoading={loader.peakCountType}
                                handleEvent={() => handleWidgetOnClick('peakConcurrencyQueryCount')}
                                tooltipLabel=""
                                bgColor='#ffffff'
                                color='#000000'
                                valueFontWeight={300}
                            />
                        </DashboardCard>
                    </Grid>
                </Box>
            </Box>
        </PageWrapper>
    );
};

export default DBsql;
